import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Type-to-search single-select (combobox). A text input filters a list of
 * options inline; pick with the mouse or the keyboard (↑/↓/Enter, Esc to close).
 *
 * Two usage modes:
 * - Persistent selection (e.g. the customer box): pass `value`; when closed the
 *   input shows the selected option's label.
 * - "Pick to add" (e.g. mapping a Facil-IT item to a row): pass `resetOnSelect`
 *   so the box clears after each pick and is ready for the next one.
 */

export interface ComboboxOption {
  value: string;
  label: string;
  /** Extra text matched while filtering but not shown (e.g. an id or code). */
  keywords?: string;
}

const FIELD_CLASS =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-accent-500 focus:ring-2 focus:ring-accent-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400';

export function Combobox({
  options,
  value = '',
  onSelect,
  placeholder = 'Type to search…',
  disabled = false,
  className = '',
  resetOnSelect = false,
  emptyLabel = 'No matches',
}: {
  options: ComboboxOption[];
  value?: string;
  onSelect: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  resetOnSelect?: boolean;
  emptyLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(() => options.find((o) => o.value === value) ?? null, [options, value]);

  // Closed → show the selected label; open → show what the user is typing.
  const display = open ? query : selected?.label ?? '';

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) || (o.keywords ?? '').toLowerCase().includes(q),
    );
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Reset the highlight whenever the visible list changes.
  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  const choose = (opt: ComboboxOption) => {
    onSelect(opt.value);
    setQuery('');
    setOpen(false);
    if (!resetOnSelect) inputRef.current?.blur();
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={open}
        autoComplete="off"
        value={display}
        disabled={disabled}
        placeholder={placeholder}
        onFocus={() => {
          setQuery('');
          setOpen(true);
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!open) setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!open) setOpen(true);
            else setHighlight((h) => Math.min(h + 1, filtered.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
          } else if (e.key === 'Enter') {
            if (open && filtered[highlight]) {
              e.preventDefault();
              choose(filtered[highlight]);
            }
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
        className={FIELD_CLASS}
      />
      {open && (
        <ul className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1 text-sm shadow-lg">
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-slate-400">{emptyLabel}</li>
          ) : (
            filtered.map((opt, i) => (
              <li key={opt.value}>
                <button
                  type="button"
                  // mousedown (not click) fires before the input's blur closes the list.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose(opt);
                  }}
                  onMouseEnter={() => setHighlight(i)}
                  className={`block w-full px-3 py-1.5 text-left ${
                    i === highlight ? 'bg-accent-50 text-accent-800' : 'text-slate-700 hover:bg-slate-50'
                  } ${opt.value === value ? 'font-semibold' : ''}`}
                >
                  {opt.label}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
